import { useCallback } from 'react';
import { TValidationMethod } from 'js/utils/Validation/types';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import * as helpers from './helpers';
import { useGetFormFieldValue } from '../../../../hooks';
import { useWithRecordLevelValidation } from '../useWithRecordLevelValidation';

export const useItalianIdFieldValidation = ({ fieldName }: { fieldName: string }) => {
    const translate = useComponentTranslations();
    const getFormFieldValue = useGetFormFieldValue();
    const withRecordLevelValidation = useWithRecordLevelValidation({ fieldName });

    const italianIdValidate = useCallback<TValidationMethod<string, Record<string, string>>>(async (value, formValues) => {
        const firstname = getFormFieldValue('firstname') || '';
        const lastname = getFormFieldValue('lastname') || '';
        const sex = getFormFieldValue('sex') || '';
        const birthdate = getFormFieldValue('birthdate') || '';
        const countryOfBirth = getFormFieldValue('countryOfBirth');

        const inputCode = value.slice(0, 15);
        const inputCodesCheckChar = value.slice(-1);
        const birthDayFromCode = value.slice(9, 11);
        const expectedInputCheckChar = helpers.getCheckSumCharacter(inputCode);

        if (inputCodesCheckChar !== expectedInputCheckChar || !/^[A-Z]{6}[A-Z0-9]{2}[A-Z][A-Z0-9]{6}$/.test(inputCode)) {
            return translate('playtech.mobile.system.form.italianID.internal.invalidId');
        }

        const [yyyy, mm, dd] = birthdate.split('-');
        const yy = yyyy.substring(2);

        const generatedCode = [
            helpers.getName3LetterCode(lastname),
            helpers.getName3LetterCode(firstname, true),
            yy || '__',
            helpers.getMonthLetter(mm) || '_',
            helpers.getBirthDayWithGenderCode(dd, sex) || '__',
            (countryOfBirth === 'IT' ? formValues.cityOfficialCode : formValues.countryRegulationCode) || '____'
        ].join('');

        let incorrectFields = [];
        const diffIndexes = helpers.compareItalianIDs(inputCode, generatedCode) || [];
        // lastname diff
        if (diffIndexes.includes(0) || diffIndexes.includes(1) || diffIndexes.includes(2)) {
            incorrectFields.push(translate('playtech.mobile.system.form.lastname.label'));
        }
        // firstname diff
        if (diffIndexes.includes(3) || diffIndexes.includes(4) || diffIndexes.includes(5)) {
            incorrectFields.push(translate('playtech.mobile.system.form.firstname.label'));
        }
        // birthdate diff
        if (diffIndexes.includes(6) || diffIndexes.includes(7) || diffIndexes.includes(8)) {
            incorrectFields.push(translate('playtech.mobile.system.form.birthdate.label'));
        }
        // birthdate and/or gender diff
        if (diffIndexes.includes(9) || diffIndexes.includes(10)) {
            const birthDayFromCodeAsNumber = Number(helpers.italianIdLetterToDigit(birthDayFromCode));
            const isIncorrectGender = birthDayFromCodeAsNumber > 40 ? sex !== 'F' : sex !== 'M';
            const isIncorrectBirthday = birthDayFromCodeAsNumber > 40
                ? birthDayFromCodeAsNumber - 40 !== Number(dd)
                : birthDayFromCodeAsNumber !== Number(dd);
            isIncorrectGender && incorrectFields.push(translate('playtech.mobile.system.form.gender.placeholder'));
            isIncorrectBirthday && incorrectFields.push(translate('playtech.mobile.system.form.birthdate.label'));
        }
        // country or city diff
        if (diffIndexes.includes(11) || diffIndexes.includes(12) || diffIndexes.includes(13) || diffIndexes.includes(14)) {
            incorrectFields.push(countryOfBirth === 'IT'
                ? translate('playtech.mobile.system.form.placeOfBirth.cityOfBirth.label')
                : translate('playtech.mobile.system.form.placeOfBirth.countryOfBirth.label')
            );
        }

        incorrectFields = [...new Set(incorrectFields)];

        if (incorrectFields.length) {
            return translate('playtech.mobile.system.form.italianID.internal.wrongData', [incorrectFields.join(', ')]);
        }

        if (diffIndexes.length !== 0) {
            return translate('playtech.mobile.system.form.italianID.internal.invalidId');
        }
    }, [getFormFieldValue, translate]);

    return withRecordLevelValidation(italianIdValidate);
};
